import {
    DescriptionSubTypography,
    PollSectionTitleTypography,
    PostPollOptionField,
} from "../../PostForm.styles";
import { Button, IconButton, Stack } from "@mui/material";
import { SortableList } from "../../../../SortableList/SortableList";
import { SquarePollSolidIcon } from "@icons/SquarePollSolidIcon";
import { CircleXMarkIcon } from "@icons/CircleXMarkIcon";
import { DragHandle } from "../../../../SortableList/SortableDragHandle";
import React, { ChangeEvent } from "react";
import { PollTabSectionStack } from "../PollTab.styles";
import { PollOptionType, usePollOptionsState } from "../PollOptionsState";
import { PollTypesEnum } from "../../../../../Shared/Types/Polls";
import { CircleCheckSolidIcon } from "@icons/CircleCheckSolidIcon";
import { OptionCircleIcon } from "../../../PostPoll/Options/Icons/OptionCircleIcon";

export function TextOptionsSection({
    allowPollEdit,
}: {
    allowPollEdit: boolean;
}) {
    const {
        pollType,
        pollOptions,
        updatePollOption,
        removePollOption,
        resetPollOptions,
    } = usePollOptionsState();

    const handleInputChange = (
        index: number,
        event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    ) => {
        updatePollOption(index, event.target.value);
    };

    const handleRemoveInput = (index: number) => {
        removePollOption(index);
    };

    return (
        <PollTabSectionStack>
            <PollSectionTitleTypography>
                Antworten (mind. 2 erforderlich)
            </PollSectionTitleTypography>
            {pollType === PollTypesEnum.POLL_QUIZ && (
                <DescriptionSubTypography>
                    Kennzeichne die richtige Antwort
                </DescriptionSubTypography>
            )}
            <Stack gap={"12px"}>
                <SortableList
                    items={pollOptions}
                    onChange={(options) => resetPollOptions(options)}
                    onDragAndDropChanged={() => {
                        if (
                            document.activeElement instanceof
                                HTMLInputElement ||
                            document.activeElement instanceof
                                HTMLTextAreaElement
                        ) {
                            document.activeElement.blur();
                        }
                    }}
                    renderItem={({ id, content, isCorrectAnswer }) => (
                        <Stack flexDirection="row" alignItems="center">
                            <PostPollOptionField
                                key={id}
                                fullWidth
                                disabled={!allowPollEdit}
                                type="text"
                                value={content}
                                variant="outlined"
                                inputProps={{ maxLength: 100 }}
                                onChange={(event) =>
                                    handleInputChange(id, event)
                                }
                                placeholder={`Option`}
                                InputLabelProps={{
                                    shrink: content.length > 0,
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <OptionIcon
                                            isCorrectAnswer={isCorrectAnswer}
                                            content={content}
                                            id={id}
                                        />
                                    ),
                                    endAdornment: (
                                        <>
                                            {content.length > 0 &&
                                                allowPollEdit && (
                                                    <Button
                                                        onClick={() =>
                                                            handleRemoveInput(
                                                                id,
                                                            )
                                                        }
                                                        tabIndex={-1}
                                                        sx={{
                                                            width: "24px",
                                                            height: "24px",
                                                            minWidth: "24px",
                                                            borderRadius:
                                                                "90px",
                                                        }}
                                                    >
                                                        <CircleXMarkIcon
                                                            width={"24px"}
                                                            height={"24px"}
                                                            color={
                                                                "var(--community-color-transparent-black-20)"
                                                            }
                                                        />
                                                    </Button>
                                                )}
                                        </>
                                    ),
                                }}
                            />
                            <DragHandle />
                        </Stack>
                    )}
                />
            </Stack>
        </PollTabSectionStack>
    );
}

function OptionIcon({ content, id, isCorrectAnswer }: PollOptionType) {
    const { pollType, changeAnswer } = usePollOptionsState();

    switch (pollType) {
        case PollTypesEnum.POLL_VISIBLE:
        case PollTypesEnum.POLL_HIDDEN:
            return (
                <SquarePollSolidIcon
                    width="16px"
                    height="16px"
                    color={"var(--community-color-primary-text)"}
                />
            );
        case PollTypesEnum.POLL_QUIZ:
            return (
                <IconButton
                    disabled={content.length === 0}
                    onClick={() => changeAnswer(id)}
                >
                    {isCorrectAnswer ? (
                        <CircleCheckSolidIcon color="var(--community-color-primary-text)" />
                    ) : (
                        <OptionCircleIcon
                            color={
                                content.length === 0
                                    ? "var(--community-color-dark-gray)"
                                    : "var(--community-color-transparent-black-50)"
                            }
                        />
                    )}
                </IconButton>
            );
        default:
            return null;
    }
}
